import * as types from '../mutation-types'

// state
export const state = {
  shipping: null,
  city: null,
  pack: null,
  complete: null,
  file: null,
  mobile: null,
  week: null,
  leaflet: null,
  tru: null,
  gift: null,
  giftType: null,
  changelocation: null,
  location: null,
  qrcode: null,
  // New fields for enhanced workflow
  participantId: null,
  participationId: null,
  termsAccepted: false,
  helmetWon: null,
  helmetCode: null,
  pubgCode: null,
  majorPrizeWon: null,
  spinCompleted: false,
  spinCompletedAt: null, // Timestamp when spin was completed
  spinSessionId: null, // Session ID for claiming spin results
  // Spin claiming state
  spinClaimed: false, // Whether spin result has been claimed
  spinClaimedAt: null, // When spin was claimed
  actualMajorPrize: null, // Actual major prize type after claiming (not 'unknown')
  claimingInProgress: false, // Loading state for claiming operations
  claimError: null, // Last claiming error
  arMediaPath: null,
  arMediaType: null,
  socialShared: false,
  socialPlatforms: [],
  email: null,
  fullname: null,
  age: null,
  gender: null,
  currentStep: -1,  // -1 means not in wizard (welcome page), shows default orange header
  stepCount: 2, // Simple workflow: 2 = skip personal details (returning user), 3 = completed with spin
  skippedSteps: [], // Deprecated - kept for backward compatibility
  prize: null, // Prize data from spin wheel
  // Loading states for duplicate prevention
  isSubmittingMobile: false,
  isSubmittingDetails: false,
  isCompletingParticipation: false,
  // Request tracking for duplicate prevention
  lastMobileRequest: null,
  lastDetailsRequest: null,
  lastCompletionRequest: null,
  // AR fullscreen state
  arFullscreenActive: false
}

// getters
export const getters = {
  // Individual property getters (backward compatibility)
  shipping: state => state.shipping,
  city: state => state.city,
  pack: state => state.pack,
  complete: state => state.complete,
  file: state => state.file,
  mobile: state => state.mobile,
  week: state => state.week,
  leaflet: state => state.leaflet,
  tru: state => state.tru,
  gift: state => state.gift,
  giftType: state => state.giftType,
  changelocation: state => state.changelocation,
  location: state => state.location,
  qrcode: state => state.qrcode,

  // Enhanced getters
  getWizardProperty: (state) => (property) => state[property],
  getWizardData: (state) => state,
  isWizardComplete: (state) => Boolean(state.complete),
  hasQrCode: (state) => Boolean(state.qrcode),
  hasMobile: (state) => Boolean(state.mobile),
  hasLocation: (state) => Boolean(state.location || state.city),
  
  // New workflow getters
  participantId: state => state.participantId,
  participationId: state => state.participationId,
  termsAccepted: state => state.termsAccepted,
  helmetWon: state => state.helmetWon,
  pubgCode: state => state.pubgCode,
  majorPrizeWon: state => state.majorPrizeWon,
  spinCompleted: state => state.spinCompleted,
  spinCompletedAt: state => state.spinCompletedAt,
  spinSessionId: state => state.spinSessionId,
  // Claiming state getters
  spinClaimed: state => state.spinClaimed,
  spinClaimedAt: state => state.spinClaimedAt,
  actualMajorPrize: state => state.actualMajorPrize,
  claimingInProgress: state => state.claimingInProgress,
  claimError: state => state.claimError,
  // Combined getters for convenience
  hasPendingSpinResult: state => state.spinCompleted && !state.spinClaimed,
  hasClaimedSpinResult: state => state.spinCompleted && state.spinClaimed,
  hasActualMajorPrize: state => state.actualMajorPrize && state.actualMajorPrize !== 'unknown',
  arMediaPath: state => state.arMediaPath,
  arMediaType: state => state.arMediaType,
  socialShared: state => state.socialShared,
  socialPlatforms: state => state.socialPlatforms,
  email: state => state.email,
  fullname: state => state.fullname,
  age: state => state.age,
  gender: state => state.gender,
  currentStep: state => state.currentStep,
  stepCount: state => state.stepCount,
  skippedSteps: state => state.skippedSteps,
  prize: state => state.prize,

  // Progress calculation for new workflow
  wizardProgress: (state) => {
    const totalSteps = 7 // Total steps in new workflow
    return (state.currentStep - 1) / totalSteps
  },

  // Get required but missing steps
  requiredSteps: (state) => {
    return Object.entries(state)
      .filter(([key, value]) => !value && key !== 'complete')
      .map(([key]) => key)
  },

  // Check if wizard can proceed
  canProceed: (state) => {
    return Boolean(state.mobile && state.qrcode)
  },

  // Get all filled data
  filledData: (state) => {
    return Object.entries(state)
      .filter(([key, value]) => value !== null)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
  },

  // Loading state getters for duplicate prevention
  isSubmittingMobile: (state) => state.isSubmittingMobile,
  isSubmittingDetails: (state) => state.isSubmittingDetails,
  isCompletingParticipation: (state) => state.isCompletingParticipation,
  isAnyRequestInProgress: (state) => {
    return state.isSubmittingMobile || state.isSubmittingDetails || state.isCompletingParticipation
  },

  // Request tracking getters
  canSubmitMobileRequest: (state) => {
    if (state.isSubmittingMobile) return false
    if (!state.lastMobileRequest) return true
    // Allow request if more than 3 seconds have passed
    return Date.now() - state.lastMobileRequest > 3000
  },
  canSubmitDetailsRequest: (state) => {
    if (state.isSubmittingDetails) return false
    if (!state.lastDetailsRequest) return true
    // Allow request if more than 3 seconds have passed  
    return Date.now() - state.lastDetailsRequest > 3000
  },
  canCompleteParticipation: (state) => {
    if (state.isCompletingParticipation) return false
    if (!state.lastCompletionRequest) return true
    // Allow request if more than 5 seconds have passed
    return Date.now() - state.lastCompletionRequest > 5000
  },

  // AR fullscreen getters
  arFullscreenActive: (state) => state.arFullscreenActive,
  isArFullscreenActive: (state) => state.arFullscreenActive,

  // Prize code getter (generic, not helmet-specific)
  helmetCode: (state) => state.helmetCode
}

// Generic mutation function
const createSetter = (property) => (state, payload) => {
  const key = Object.keys(payload)[0];
  state[key] = payload[key];
};

// DEPRECATED: Old PUBG workflow calculation - no longer used
// Backend now controls step_count: 2 = skip personal, 3 = completed
const calculateStepCount = (skippedSteps) => {
  // Always return 2 (skip personal) for backward compatibility
  // Backend will set to 3 when spin completes
  return 2
}

// mutations
export const mutations = {
  // Legacy compatibility
  [types.SET] (state, { shipping }) {
    state.shipping = shipping
  },
  
  // Consolidated setters - using same mutation for similar operations
  [types.SET_SHIPPING]: createSetter('shipping'),
  [types.SET_CITY]: createSetter('city'),
  [types.SET_PACK]: createSetter('pack'),
  [types.SET_COMPLETE]: createSetter('complete'),
  [types.SET_FILE]: createSetter('file'),
  [types.SET_MOBILE]: createSetter('mobile'),
  [types.SET_WEEK]: createSetter('week'),
  [types.SET_LEAFLET]: createSetter('leaflet'),
  [types.SET_TRU]: createSetter('tru'),
  [types.SET_GIFT]: createSetter('gift'),
  [types.SET_GIFTTYPE]: createSetter('giftType'),
  [types.SET_CHANGE_LOCATION]: createSetter('changelocation'),
  [types.SET_LOCATION]: createSetter('location'),
  [types.SET_QRCODE]: createSetter('qrcode'),
  
  // New workflow mutations
  setParticipantId(state, id) {
    state.participantId = id
  },
  setParticipationId(state, id) {
    state.participationId = id
  },
  setTermsAccepted(state, accepted) {
    state.termsAccepted = accepted
  },
  setHelmetWon(state, helmet) {
    state.helmetWon = helmet
  },
  setHelmetCode(state, code) {
    state.helmetCode = code
  },
  setPubgCode(state, code) {
    state.pubgCode = code
  },
  setMajorPrizeWon(state, prize) {
    state.majorPrizeWon = prize
  },
  setSpinCompleted(state, completed) {
    state.spinCompleted = completed
  },
  setSpinCompletedAt(state, timestamp) {
    state.spinCompletedAt = timestamp
  },
  setSpinSessionId(state, sessionId) {
    state.spinSessionId = sessionId
  },
  // Spin claiming mutations
  setSpinClaimed(state, claimed) {
    state.spinClaimed = claimed
  },
  setSpinClaimedAt(state, timestamp) {
    state.spinClaimedAt = timestamp
  },
  setActualMajorPrize(state, prize) {
    state.actualMajorPrize = prize
  },
  setClaimingInProgress(state, inProgress) {
    state.claimingInProgress = inProgress
  },
  setClaimError(state, error) {
    state.claimError = error
  },
  // Combined mutation for claiming success
  setSpinClaimedSuccess(state, { claimedAt, actualMajorPrize, helmetCode, pubgCode }) {
    state.spinClaimed = true
    state.spinClaimedAt = claimedAt
    state.actualMajorPrize = actualMajorPrize
    state.claimingInProgress = false
    state.claimError = null
    // Update codes if provided
    if (helmetCode) state.helmetCode = helmetCode
    if (pubgCode) state.pubgCode = pubgCode
  },
  setArMedia(state, { path, type }) {
    state.arMediaPath = path
    state.arMediaType = type
  },
  setSocialShared(state, shared) {
    state.socialShared = shared
  },
  setSocialPlatforms(state, platforms) {
    state.socialPlatforms = platforms
  },
  setEmail(state, email) {
    state.email = email
  },
  setFullname(state, fullname) {
    state.fullname = fullname
  },
  setAge(state, age) {
    state.age = age
  },
  setGender(state, gender) {
    state.gender = gender
  },
  setPrize(state, prize) {
    state.prize = prize
  },
  setCurrentStep(state, step) {
    state.currentStep = step
  },
  incrementStep(state) {
    state.currentStep++
  },
  decrementStep(state) {
    if (state.currentStep > 1) {
      state.currentStep--
    }
  },
  setStepCount(state, count) {
    console.log(`⚠️ Manual stepCount override attempted: ${state.stepCount} → ${count}`)
    const calculatedCount = calculateStepCount(state.skippedSteps)
    if (count !== calculatedCount && state.skippedSteps.length > 0) {
      console.log(`🚫 Rejecting stepCount override. Calculated: ${calculatedCount}, Attempted: ${count}`)
      console.log(`🚫 Skipped steps: [${state.skippedSteps.join(', ')}]`)
      state.stepCount = calculatedCount
    } else {
      state.stepCount = count
      console.log(`✅ stepCount set to: ${count}`)
    }
  },
  addSkippedStep(state, stepNumber) {
    console.log(`📝 Adding skipped step: ${stepNumber}`)
    if (!state.skippedSteps.includes(stepNumber)) {
      state.skippedSteps.push(stepNumber)
      console.log(`📝 Skipped steps now: [${state.skippedSteps.join(', ')}]`)
    }
    // Use helper function to calculate correct step count
    state.stepCount = calculateStepCount(state.skippedSteps)
    console.log(`📊 Final stepCount set to: ${state.stepCount}`)
  },

  // Generic setter for any wizard property
  SET_WIZARD_PROPERTY(state, { property, value }) {
    if (state.hasOwnProperty(property)) {
      state[property] = value;
    }
  },

  // Batch update multiple properties
  SET_WIZARD_BATCH(state, payload) {
    Object.keys(payload).forEach(key => {
      if (state.hasOwnProperty(key)) {
        state[key] = payload[key];
      }
    });
  },

  // Reset state mutation (alias for resetWizard action)
  resetState(state) {
    console.log('🔄 resetState mutation called - delegating to resetWizard logic')
    // Calculate correct step count before reset
    const preservedStepCount = calculateStepCount(state.skippedSteps)
    const preservedSkippedSteps = [...state.skippedSteps]
    
    // Reset all properties but preserve step count logic
    Object.assign(state, {
      shipping: null,
      city: null,
      pack: null,
      complete: null,
      file: null,
      mobile: null,
      week: null,
      leaflet: null,
      tru: null,
      gift: null,
      giftType: null,
      changelocation: null,
      location: null,
      qrcode: null,
      participantId: null,
      termsAccepted: false,
      helmetWon: null,
      pubgCode: null,
      majorPrizeWon: null,
      spinCompleted: false,
      spinSessionId: null,
      // Reset claiming state
      spinClaimed: false,
      spinClaimedAt: null,
      actualMajorPrize: null,
      claimingInProgress: false,
      claimError: null,
      arMediaPath: null,
      arMediaType: null,
      socialShared: false,
      socialPlatforms: [],
      email: null,
      fullname: null,
      age: null,
      gender: null,
      prize: null,
      currentStep: -1,  // -1 means not in wizard (welcome page), shows default orange header
      stepCount: preservedStepCount, // ✅ Preserve calculated step count
      skippedSteps: preservedSkippedSteps, // ✅ Preserve skipped steps
      arFullscreenActive: false
    })

    console.log(`✅ resetState completed - stepCount preserved as: ${preservedStepCount}`)
  },

  // Loading state mutations for duplicate prevention
  setSubmittingMobile(state, isSubmitting) {
    state.isSubmittingMobile = isSubmitting
    if (isSubmitting) {
      state.lastMobileRequest = Date.now()
    }
  },
  setSubmittingDetails(state, isSubmitting) {
    state.isSubmittingDetails = isSubmitting
    if (isSubmitting) {
      state.lastDetailsRequest = Date.now()
    }
  },
  setCompletingParticipation(state, isCompleting) {
    state.isCompletingParticipation = isCompleting
    if (isCompleting) {
      state.lastCompletionRequest = Date.now()
    }
  },
  clearRequestTracking(state) {
    state.isSubmittingMobile = false
    state.isSubmittingDetails = false
    state.isCompletingParticipation = false
    state.lastMobileRequest = null
    state.lastDetailsRequest = null
    state.lastCompletionRequest = null
  },

  // AR fullscreen mutations
  setArFullscreen(state, isActive) {
    state.arFullscreenActive = isActive
  },
  toggleArFullscreen(state) {
    state.arFullscreenActive = !state.arFullscreenActive
  }
}


// actions
export const actions = {
  saveShipping ({ commit, dispatch }, payload) {
    commit(types.SET_SHIPPING, payload)
  },
  saveCity ({ commit, dispatch }, payload) {
    commit(types.SET_CITY, payload)
  },
  savePack ({ commit, dispatch }, payload) {
    commit(types.SET_PACK, payload)
  },
  saveComplete ({ commit, dispatch }, payload) {
    commit(types.SET_COMPLETE, payload)
  },
  saveFile ({ commit, dispatch }, payload) {
    commit(types.SET_FILE, payload)
  },
  saveMobile ({ commit, dispatch }, payload) {
    commit(types.SET_MOBILE, payload)
  },
  saveWeek ({ commit, dispatch }, payload) {
    commit(types.SET_WEEK, payload)
  },
  saveLeaflet ({ commit, dispatch }, payload) {
    commit(types.SET_LEAFLET, payload)
  },
  saveTru ({ commit, dispatch }, payload) {
    commit(types.SET_TRU, payload)
  },
  saveGift ({ commit, dispatch }, payload) {
    commit(types.SET_GIFT, payload)
  },
  saveGiftType ({ commit, dispatch }, payload) {
    commit(types.SET_GIFTTYPE, payload)
  },
  saveChangeLocation ({ commit, dispatch }, payload) {
    commit(types.SET_CHANGE_LOCATION, payload)
  },
  saveLocation ({ commit, dispatch }, payload) {
    commit(types.SET_LOCATION, payload)
  },
  saveQrcode ({ commit, dispatch }, payload) {
    commit(types.SET_QRCODE, payload)
  },

  // Generic actions for new mutations
  setWizardProperty({ commit }, { property, value }) {
    commit('SET_WIZARD_PROPERTY', { property, value });
  },

  setWizardBatch({ commit }, payload) {
    commit('SET_WIZARD_BATCH', payload);
  },

  // Reset wizard state (preserves step count calculation)
  resetWizard({ commit, state }) {
    console.log('🔄 resetWizard called - preserving step count logic')
    console.log(`🔄 Current skippedSteps: [${state.skippedSteps.join(', ')}], stepCount: ${state.stepCount}`)
    
    // Calculate correct step count based on current skipped steps before reset
    const preservedStepCount = calculateStepCount(state.skippedSteps)
    const preservedSkippedSteps = [...state.skippedSteps] // Clone array
    
    console.log(`🔄 Preserving stepCount: ${preservedStepCount}, skippedSteps: [${preservedSkippedSteps.join(', ')}]`)
    
    commit('SET_WIZARD_BATCH', {
      shipping: null,
      city: null,
      pack: null,
      complete: null,
      file: null,
      mobile: null,
      week: null,
      leaflet: null,
      tru: null,
      gift: null,
      giftType: null,
      changelocation: null,
      location: null,
      qrcode: null,
      participantId: null,
      termsAccepted: false,
      helmetWon: null,
      pubgCode: null,
      majorPrizeWon: null,
      spinCompleted: false,
      spinSessionId: null,
      // Reset claiming state
      spinClaimed: false,
      spinClaimedAt: null,
      actualMajorPrize: null,
      claimingInProgress: false,
      claimError: null,
      arMediaPath: null,
      arMediaType: null,
      socialShared: false,
      socialPlatforms: [],
      email: null,
      fullname: null,
      age: null,
      gender: null,
      prize: null,
      currentStep: -1,  // -1 means not in wizard (welcome page), shows default orange header
      stepCount: preservedStepCount, // ✅ Preserve calculated step count
      skippedSteps: preservedSkippedSteps, // ✅ Preserve skipped steps
      arFullscreenActive: false
    });

    console.log(`✅ resetWizard completed - stepCount preserved as: ${preservedStepCount}`)
  },
  
  // New enhanced actions
  nextStep({ commit, state }) {
    commit('incrementStep')
  },
  
  previousStep({ commit }) {
    commit('decrementStep')
  },
  
  goToStep({ commit }, step) {
    commit('setCurrentStep', step)
  }
}

// Default export
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
